import { useEffect, useRef, useState } from "react";

const ELFSIGHT_SRC = "https://elfsightcdn.com/platform.js";
const APP_CLASS = "elfsight-app-6df88370-a72b-4e0e-8597-ab863c84a92b";

/** Injects the Elfsight platform script once, only when a widget is near the viewport. */
const loadElfsight = () => {
  if (document.querySelector(`script[src="${ELFSIGHT_SRC}"]`)) return;
  const s = document.createElement("script");
  s.src = ELFSIGHT_SRC;
  s.async = true;
  s.defer = true;
  document.body.appendChild(s);
};

type Props = {
  /** Section heading. Pass null to render the widget with no heading block. */
  heading?: string | null;
  eyebrow?: string | null;
  subheading?: string | null;
  /** "dark" = on brand/burgundy backgrounds, "light" = on white/cream, "bare" = no section chrome. */
  variant?: "dark" | "light" | "bare";
  /** Override the section background (e.g. "transparent" when nested in a themed section). */
  background?: string;
  className?: string;
};

const GoogleReviews = ({
  heading = "Real Stories. Real Recovery.",
  eyebrow = "Reviews",
  subheading = "Verified Google reviews from clients and families of Cherry Hill Recovery Center.",
  variant = "light",
  background,
  className = "",
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      loadElfsight();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          loadElfsight();
          io.disconnect();
        }
      },
      { rootMargin: "300px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const dark = variant === "dark";

  const inner = (
    <div ref={ref} className="max-w-5xl mx-auto" style={{ minHeight: visible ? undefined : 320 }}>
      {visible ? (
        <div className={APP_CLASS} data-elfsight-app-lazy />
      ) : (
        <div
          aria-hidden
          className="rounded-xl"
          style={{
            minHeight: 320,
            background: dark ? "rgba(255,255,255,0.06)" : "rgba(139,26,26,0.04)",
          }}
        />
      )}
    </div>
  );

  if (variant === "bare") return <div className={className}>{inner}</div>;

  return (
    <section
      id="reviews"
      className={`py-16 md:py-20 ${dark ? "text-primary-foreground" : ""} ${className}`}
      style={{ background: background ?? (dark ? "#6B1414" : "#FAF4F0") }}
    >
      <div className="container">
        {(eyebrow || heading || subheading) && (
          <div className="text-center mb-10">
            {eyebrow && (
              <p
                className="font-semibold text-sm tracking-wider uppercase mb-3"
                style={{ color: dark ? "#E9C46A" : "#8B1A1A" }}
              >
                {eyebrow}
              </p>
            )}
            {heading && (
              <h2
                className="font-display text-3xl md:text-4xl font-bold"
                style={{ color: dark ? "#FFFFFF" : "#8B1A1A" }}
              >
                {heading}
              </h2>
            )}
            {subheading && (
              <p
                className="mt-4 max-w-2xl mx-auto"
                style={{ color: dark ? "rgba(255,255,255,0.8)" : "#555555" }}
              >
                {subheading}
              </p>
            )}
          </div>
        )}
        {inner}
      </div>
    </section>
  );
};

export default GoogleReviews;
